import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../api/client';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PrecedentResult {
  claim_id: string;
  claim_number: string;
  tier_claim_num: string;
  status: string;
  lob: string;
  coverage_type: string;
  handler_name: string;
  company_name: string;
  claimant: string;
  loss_date: string | null;
  closed_date: string | null;
  total_outstanding: number;
  total_paid: number;
  total_incurred: number;
  controlling_state: string;
  ai_summary: string;
}

interface SearchResponse {
  results: PrecedentResult[];
  count: number;
  query: string;
  ai_summary_available: boolean;
  ai_model?: string;
  ai_latency_ms?: number;
  message?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const LOB_OPTIONS = [
  { value: '', label: 'All LOBs' },
  { value: 'PL', label: 'Professional Liability' },
  { value: 'GL', label: 'General Liability' },
  { value: 'WC', label: "Workers' Comp" },
  { value: 'AL', label: 'Auto Liability' },
  { value: 'PR', label: 'Property' },
];

const COVERAGE_OPTIONS = [
  { value: '', label: 'All Coverage' },
  { value: 'PROF', label: 'Professional' },
  { value: 'GENL', label: 'General' },
  { value: 'CORO', label: 'Corporate' },
  { value: 'EPLI', label: 'EPLI' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function PrecedentSearch() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [lob, setLob] = useState(searchParams.get('lob') || '');
  const [coverageType, setCoverageType] = useState(searchParams.get('coverage') || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SearchResponse | null>(null);

  async function handleSearch() {
    const trimmed = query.trim();
    if (!trimmed) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const data = await api.precedentSearch({
        query: trimmed,
        lob: lob || undefined,
        coverage_type: coverageType || undefined,
        limit: 10,
      });
      setResult(data);
    } catch (err: any) {
      setError(err.message || 'Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && query.trim()) handleSearch();
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Find Similar Claims</h1>
        <p className="text-sm text-gray-500 mt-1">
          Search historical claims to find precedent for handling similar situations.
        </p>
      </div>

      {/* Search Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 space-y-4 mb-6">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1.5">
            Describe the situation
          </label>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="e.g., dental malpractice missed diagnosis, slip and fall grocery store..."
            disabled={loading}
            className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          />
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">LOB</label>
            <select
              value={lob}
              onChange={(e) => setLob(e.target.value)}
              disabled={loading}
              className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {LOB_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Coverage</label>
            <select
              value={coverageType}
              onChange={(e) => setCoverageType(e.target.value)}
              disabled={loading}
              className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {COVERAGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <button
            onClick={handleSearch}
            disabled={loading || !query.trim()}
            className="inline-flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {loading ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Searching...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Search
              </>
            )}
          </button>
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex flex-col items-center justify-center gap-4 py-16">
          <svg className="animate-spin h-10 w-10 text-blue-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
            <path className="opacity-80" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
          </svg>
          <div className="text-center">
            <p className="text-sm font-semibold text-blue-700">Searching historical claims...</p>
            <p className="text-xs text-gray-400 mt-1">
              AI is analyzing matching claims for precedent summaries. This may take 10-20 seconds.
            </p>
          </div>
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-6">
          <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <div>
            <p className="text-sm font-semibold text-red-800">Search failed</p>
            <p className="text-xs text-red-600 mt-0.5">{error}</p>
          </div>
        </div>
      )}

      {/* Results */}
      {result && !loading && (
        <div>
          {/* Results header */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              {result.count === 0
                ? 'No Results'
                : `${result.count} Similar Claim${result.count === 1 ? '' : 's'} Found`}
            </h2>
            {result.ai_summary_available && result.ai_model && (
              <span className="text-xs text-gray-400">
                AI: {result.ai_model.split('.').pop()}
                {result.ai_latency_ms && ` · ${(result.ai_latency_ms / 1000).toFixed(1)}s`}
              </span>
            )}
          </div>

          {/* No results message */}
          {result.count === 0 && result.message && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
              <svg className="w-10 h-10 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <p className="text-sm text-gray-600">{result.message}</p>
            </div>
          )}

          {/* Result cards */}
          <div className="space-y-3">
            {result.results.map((claim, i) => (
              <div
                key={claim.claim_id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 hover:border-blue-300 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Claim header */}
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-gray-400">{i + 1}.</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {claim.tier_claim_num || claim.claim_number}
                      </span>
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600">
                        {claim.coverage_type}/{claim.lob}
                      </span>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                          claim.status === 'Closed'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {claim.status}
                      </span>
                      {claim.controlling_state && (
                        <span className="text-xs text-gray-400">{claim.controlling_state}</span>
                      )}
                    </div>

                    {/* Meta row */}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mb-2">
                      <span>Handler: {claim.handler_name || '—'}</span>
                      <span>Company: {claim.company_name || '—'}</span>
                      {claim.loss_date && <span>Loss: {new Date(claim.loss_date).toLocaleDateString()}</span>}
                      {claim.closed_date && <span>Closed: {new Date(claim.closed_date).toLocaleDateString()}</span>}
                    </div>

                    {/* AI Summary */}
                    {claim.ai_summary && (
                      <p className="text-sm text-gray-700 leading-relaxed">{claim.ai_summary}</p>
                    )}
                  </div>

                  {/* Financial + action */}
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-900">
                        {formatCurrency(claim.total_paid)} paid
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatCurrency(claim.total_incurred)} incurred
                      </p>
                    </div>
                    <button
                      onClick={() => navigate(`/claims/${claim.claim_id}`)}
                      className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      View Claim
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* AI unavailable notice */}
          {!result.ai_summary_available && result.count > 0 && (
            <div className="mt-4 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
              <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-xs text-amber-700">
                AI summaries were unavailable for this search. Results are shown without analysis.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
